/* ==========================================================================
   TechNest — product-gallery.js
   Scroll-snap gallery with thumbnails, keyboard support and an optional
   lightbox. Uses native horizontal scrolling on touch so swiping feels right
   without a carousel library.
   ========================================================================== */
(function () {
  'use strict';

  const TN = window.TechNest || {};
  const utils = TN.utils;
  if (!utils) return;

  class TnProductGallery extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-tn-gallery-viewport]');
      this.slides = Array.from(this.querySelectorAll('.tn-gallery__slide'));
      this.thumbs = Array.from(this.querySelectorAll('[data-tn-gallery-thumb]'));
      this.counter = this.querySelector('[data-tn-gallery-counter]');
      if (!this.viewport || !this.slides.length) return;

      this.index = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));

      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.showMedia(thumb.dataset.tnGalleryThumb));
      });

      const prev = this.querySelector('[data-tn-gallery-prev]');
      const next = this.querySelector('[data-tn-gallery-next]');
      if (prev) prev.addEventListener('click', () => this.go(this.index - 1));
      if (next) next.addEventListener('click', () => this.go(this.index + 1));

      // Arrow keys move through the gallery when focus is inside it.
      this.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.go(this.index + 1);
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.go(this.index - 1);
        }
      });

      // Track which slide the shopper has scrolled to.
      if ('IntersectionObserver' in window) {
        this._observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.intersectionRatio < 0.6) return;
              const i = this.slides.indexOf(entry.target);
              if (i > -1 && i !== this.index) this.setActive(i, false);
            });
          },
          { root: this.viewport, threshold: [0.6] }
        );
        this.slides.forEach((slide) => this._observer.observe(slide));
      }

      if (this.dataset.zoom === 'true') this.initZoom();
      this.setActive(this.index, false);
    }

    disconnectedCallback() {
      if (this._observer) this._observer.disconnect();
    }

    go(index) {
      const clamped = Math.min(this.slides.length - 1, Math.max(0, index));
      this.setActive(clamped, true);
    }

    showMedia(mediaId) {
      const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      if (index > -1) this.setActive(index, true);
    }

    setActive(index, scroll) {
      this.index = index;
      const slide = this.slides[index];

      this.slides.forEach((s, i) => {
        const active = i === index;
        s.classList.toggle('is-active', active);
        if (active) s.removeAttribute('aria-hidden');
        else s.setAttribute('aria-hidden', 'true');
        // Pause any video that scrolls out of view.
        const video = s.querySelector('video');
        if (video && !active && !video.paused) video.pause();
      });

      this.thumbs.forEach((thumb) => {
        const active = thumb.dataset.tnGalleryThumb === slide.dataset.mediaId;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-current', String(active));
      });

      if (this.counter) this.counter.textContent = index + 1 + ' / ' + this.slides.length;

      if (scroll && slide) {
        slide.scrollIntoView({
          behavior: utils.prefersReducedMotion() ? 'auto' : 'smooth',
          block: 'nearest',
          inline: 'start'
        });
      }
    }

    initZoom() {
      this.lightbox = this.querySelector('[data-tn-gallery-lightbox]');
      this.lightboxContent = this.querySelector('[data-tn-gallery-lightbox-content]');
      if (!this.lightbox || !this.lightboxContent) return;

      this.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-tn-gallery-zoom]');
        if (trigger) {
          event.preventDefault();
          this.openZoom(trigger.dataset.tnGalleryZoom, trigger);
        }
        if (event.target.closest('[data-tn-gallery-lightbox-close]')) this.closeZoom();
        if (event.target === this.lightbox) this.closeZoom();
      });

      this._onKeydown = (event) => {
        if (event.key === 'Escape') this.closeZoom();
      };
    }

    openZoom(mediaId, opener) {
      const slide = this.slides.find((s) => s.dataset.mediaId === String(mediaId));
      const img = slide && slide.querySelector('img');
      if (!img) return;

      this._zoomOpener = opener;
      // Ask for a larger render of the same Shopify image.
      const large = img.currentSrc || img.src;
      const clone = document.createElement('img');
      clone.src = large.replace(/(&|\?)width=\d+/, '$1width=2000');
      clone.alt = img.alt;
      clone.className = 'tn-gallery__lightbox-img';

      this.lightboxContent.innerHTML = '';
      this.lightboxContent.appendChild(clone);

      this.lightbox.hidden = false;
      void this.lightbox.offsetWidth;
      this.lightbox.classList.add('is-open');
      utils.lockScroll();
      document.addEventListener('keydown', this._onKeydown);
      this._release = utils.trapFocus(this.lightbox);
    }

    closeZoom() {
      if (!this.lightbox || this.lightbox.hidden) return;
      this.lightbox.classList.remove('is-open');
      utils.unlockScroll();
      document.removeEventListener('keydown', this._onKeydown);
      if (this._release) this._release();
      if (this._zoomOpener && document.body.contains(this._zoomOpener)) {
        this._zoomOpener.focus({ preventScroll: true });
      }
      setTimeout(() => {
        this.lightbox.hidden = true;
        this.lightboxContent.innerHTML = '';
      }, 300);
    }
  }

  utils.define('tn-product-gallery', TnProductGallery);
})();
